/**
 * Die einzige Quelle für Query-Keys.
 *
 * Das ist Pflichtprüfung 2 aus `PLAN.md`, und sie steht bewusst *vor* dem
 * ersten Feature, das Daten lädt: TanStack Query verzeiht viel und rächt sich
 * spät. Ohne feste Konvention entstehen über zwanzig Features fünfzig Muster,
 * und irgendwann zeigt eine Zahl einen alten Wert — der Fehler, den man am
 * spätesten findet.
 *
 * ## Die drei Regeln
 *
 * **1 · Kein Key entsteht außerhalb dieser Datei.** Auch kein „nur dieses eine
 * Mal" inline. Ein Literal-Array in einer Komponente ist genau der Anfang.
 *
 * **2 · Jeder Key beginnt mit seinem Fachbereich.** Dadurch ist Invalidierung
 * über ein Präfix möglich, und die ist die einzige, die nichts vergisst:
 *
 *     queryClient.invalidateQueries({ queryKey: keys.billing.all })
 *
 * Nie einzeln aufgezählte Keys invalidieren. Wer aufzählt, vergisst — und der
 * vergessene ist der, der den alten Wert zeigt.
 *
 * **3 · Filter gehören als Objekt ans Ende**, nicht ausgebreitet. Sonst hängt
 * der Key an der Reihenfolge der Argumente, und zwei Aufrufe mit denselben
 * Filtern in anderer Reihenfolge sind für den Cache zwei verschiedene Abfragen.
 *
 * ## Aufbau
 *
 *     <bereich>.all              Präfix — für Invalidierung
 *     <bereich>.list(filter)     Liste
 *     <bereich>.detail(id)       Einzelsatz
 */

export const keys = {
  entities: {
    all: ['entities'] as const,
    list: (filter: Readonly<Record<string, unknown>>) => ['entities', 'list', filter] as const,
    detail: (id: string) => ['entities', 'detail', id] as const,
  },

  /** Feature 01 — Grundstücke. Eigenes Präfix statt `entities`: Der Baum
   * invalidiert nach dem Anlegen genau diese Domäne, nicht jede Liste, die
   * irgendeine Entity zeigt. */
  properties: {
    all: ['properties'] as const,
    list: (filter: Readonly<Record<string, unknown>>) => ['properties', 'list', filter] as const,
    detail: (id: string) => ['properties', 'detail', id] as const,
  },

  /** Spec 02a — Gebäude. Eigenes Präfix aus demselben Grund wie `properties`:
   * Ein Speichern invalidiert `entities.all` (der Baum, der Kopf) und
   * `buildings.all` (die Stammdaten dieser Art), nie einzeln aufgezählt. */
  buildings: {
    all: ['buildings'] as const,
    detail: (id: string) => ['buildings', 'detail', id] as const,
  },

  /** Spec 02a — Einheiten. */
  units: {
    all: ['units'] as const,
    detail: (id: string) => ['units', 'detail', id] as const,
    usageTypes: ['units', 'usage-types'] as const,
  },

  /** Spec 03 — Mieter. `search` trägt die eigene Fachbereichs-Vorsilbe, obwohl
   * die Abfrage nur von `features/contracts` benutzt wird (Mieterauswahl im
   * Vertragssheet): `features` dürfen einander nicht importieren, also liegt
   * der Fetcher dort lokal (wie `useArchivePreview` in `features/properties/
   * api.ts`, Spec 03 „Was 03 an Bestehendem ändert", Punkt 7) — aber der
   * Query-Key bleibt unter `tenants`, damit `useCreateTenant`/`useUpdateTenant`
   * (in `features/tenants`) ihn mit einer einzigen Präfix-Invalidierung
   * (`tenants.all`) mittreffen, ohne von `features/contracts` zu wissen. */
  tenants: {
    all: ['tenants'] as const,
    detail: (id: string) => ['tenants', 'detail', id] as const,
    search: (q: string) => ['tenants', 'search', q] as const,
    legalForms: ['tenants', 'legal-forms'] as const,
    salutations: ['tenants', 'salutations'] as const,
  },

  /** Spec 03 — Verträge. Spec 04a ergänzt `termReasons` (Enum, ändert sich
   * nie zur Laufzeit — ein fester Key ohne Filter, wie `units.usageTypes`). */
  contracts: {
    all: ['contracts'] as const,
    list: (filter: Readonly<Record<string, unknown>>) => ['contracts', 'list', filter] as const,
    detail: (id: string) => ['contracts', 'detail', id] as const,
    termReasons: ['contracts', 'term-reasons'] as const,
  },

  /** Spec 04a — Sollstellung und offene Posten. Ein Präfix für alle drei
   * Listen (`open-items`, `summary`, `contracts/{id}/charges`): Jede
   * schreibende Mutation (Lauf, Nachzug, Handkorrektur, Konditionswechsel)
   * kann alle drei zugleich verändern, deshalb invalidiert jede von ihnen
   * ausschließlich über dieses Präfix, nie einzeln aufgezählt.
   *
   * Spec 04b ergänzt `paymentMethods` (Enum, ändert sich nie zur Laufzeit,
   * wie `contracts.termReasons`) — `useCreatePayment` invalidiert weiterhin
   * nur über `billing.all`, nie einzeln aufgezählt: Eine Zahlung verändert
   * `open-items` **und** `summary` zugleich.
   *
   * Spec 04c ergänzt `unallocated` (Reiter *Klärfälle*) und `ledger`
   * (Mietkonto je Mieter). Ein Storno (`useReversePayment`) und eine
   * nachträgliche Zuordnung (`useAddAllocations`) treffen potenziell alle
   * fünf Listen dieses Präfixes zugleich (Klärfälle, Mietkonto, offene
   * Posten, Sollstellungen, `summary`) — deshalb invalidieren beide
   * ausschließlich über `billing.all`, wie jede andere Mutation hier.
   *
   * Spec 08 ergänzt `matrix` (Reiter *Zahlungsmatrix*) — eine Zahlung oder
   * eine ergänzte Zuordnung kann eine Kachel und die Spaltensumme
   * verändern, trifft aber weiterhin nur über `billing.all`, nie einzeln. */
  billing: {
    all: ['billing'] as const,
    openItems: (filter: Readonly<Record<string, unknown>>) => ['billing', 'open-items', filter] as const,
    summary: ['billing', 'summary'] as const,
    contractCharges: (contractId: string) => ['billing', 'contract-charges', contractId] as const,
    paymentMethods: ['billing', 'payment-methods'] as const,
    unallocated: (filter: Readonly<Record<string, unknown>>) => ['billing', 'unallocated', filter] as const,
    ledger: (tenantId: string, filter: Readonly<Record<string, unknown>>) =>
      ['billing', 'ledger', tenantId, filter] as const,
    matrix: (filter: Readonly<Record<string, unknown>>) => ['billing', 'matrix', filter] as const,
    upcomingMonths: (tenantId: string) => ['billing', 'upcoming-months', tenantId] as const,
    prepaid: (filter: Readonly<Record<string, unknown>>) => ['billing', 'prepaid', filter] as const,
  },

  /** Spec 05a — Personen und Zugriffe. Ein Präfix für Personenliste **und**
   * Zugriffe: `POST /access/users` mit `grant` und jede Grant-Mutation
   * verändern potenziell beides zugleich (`grant_count` einer Person hängt an
   * ihren Grants) — deshalb invalidiert jede Mutation ausschließlich über
   * `access.all`, nie einzeln aufgezählt (dasselbe Muster wie `billing`).
   *
   * Spec 05b ergänzt `personKinds` (Enum, ändert sich nie zur Laufzeit, wie
   * `roles`) und `userGrants` (die Rückrichtung — welche Objekte sieht eine
   * Person, `screen-access-user`). Beide fallen unter dasselbe Präfix, ohne
   * eigene Mutation, die sie träfe. */
  access: {
    all: ['access'] as const,
    me: ['access', 'me'] as const,
    roles: ['access', 'roles'] as const,
    /** `q` leer für die volle Liste (`screen-access`), gesetzt für die
     * servergefilterte Suche (`PersonPicker`, AK-381) — dieselbe
     * Ein-Endpunkt-mit-Filter-Bauform wie `tenants.search`. */
    users: (q = '') => ['access', 'users', q] as const,
    grants: (entityId: string) => ['access', 'grants', entityId] as const,
    personKinds: ['access', 'person-kinds'] as const,
    userGrants: (userId: string) => ['access', 'user-grants', userId] as const,
    /** Spec 06a — die Zuweisungskandidaten für `task-assignee`
     * (`GET /access/users?can_see=`), lokal definiert in
     * `features/tasks/api.ts` (`features/*` kennen einander nicht), aber
     * unter diesem Präfix: Ein geänderter Grant kann ändern, wer eine
     * Aufgabe an `entityId` sehen darf, genau wie bei `users` oben. */
    assignableUsers: (entityId: string, q = '') => ['access', 'assignable-users', entityId, q] as const,
  },

  /** Spec 06a — Aufgaben (API-Pfad `/tickets`, deshalb das Präfix `tickets`,
   * Spec 45). Ein Präfix für alle Listen (`list`, `summary`, `statuses`,
   * `priorities`) und den Einzelsatz (`detail`, Spec 45: Aufgabe aus dem
   * Kalender öffnen): Anlegen, Ändern und „Erledigt" können `list` **und**
   * `summary` zugleich verändern (ein neuer Status ändert `attention`),
   * deshalb invalidiert jede Mutation ausschließlich über dieses Präfix, nie
   * einzeln aufgezählt — dasselbe Muster wie `billing`. */
  tickets: {
    all: ['tickets'] as const,
    list: (filter: Readonly<Record<string, unknown>>) => ['tickets', 'list', filter] as const,
    detail: (id: string) => ['tickets', 'detail', id] as const,
    summary: ['tickets', 'summary'] as const,
    statuses: ['tickets', 'statuses'] as const,
    priorities: ['tickets', 'priorities'] as const,
  },

  /** Spec 06b — Termine. Ein Präfix für `list`, `templates` und
   * `recurrences`: Anlegen, Ändern, Erledigen, Deaktivieren und Aktivieren
   * können `list` verändern, deshalb invalidiert jede Mutation ausschließlich
   * über dieses Präfix, nie einzeln aufgezählt (dasselbe Muster wie
   * `tickets`/`billing`). `templates` und `recurrences` ändern sich nie zur
   * Laufzeit (feste Konstanten im Server, kein Datenbankzugriff), bekommen
   * aber trotzdem einen eigenen Key statt eines Literal-Arrays am Aufrufer
   * (Regel 1 oben). */
  dueDates: {
    all: ['due-dates'] as const,
    list: (filter: Readonly<Record<string, unknown>>) => ['due-dates', 'list', filter] as const,
    templates: ['due-dates', 'templates'] as const,
    recurrences: ['due-dates', 'recurrences'] as const,
  },

  /** Spec 07 — Kalender. Ein Fenster je `ab` (fehlt er, die laufende Woche).
   * Kein eigener Schreibvorgang hier — ein Termin entsteht über
   * `keys.dueDates.all`, eine Zahlung über `keys.billing.all` —, deshalb
   * kein `all`-Präfix für eine eigene Mutation, sondern nur zum Invalidieren
   * von außen: `features/schedule/api.ts` trifft ihn zusätzlich, damit ein
   * neu angelegter oder geänderter Termin im Kalender ohne manuellen
   * Neuladen erscheint. */
  calendar: {
    all: ['calendar'] as const,
    window: (filter: Readonly<Record<string, unknown>>) => ['calendar', 'window', filter] as const,
  },

  /** Spec 09 — Dokumente. Ein Präfix für `list` und `summary`: Anlegen,
   * Ändern, Zuordnen und Archivieren können beide zugleich verändern (ein
   * zugeordnetes Dokument verlässt „Ohne Zuordnung" und ändert `attention`),
   * deshalb invalidiert jede Mutation ausschließlich über dieses Präfix, nie
   * einzeln aufgezählt (dasselbe Muster wie `tickets`/`billing`).
   * `categories` ändern sich nie zur Laufzeit (feste Aufzählung
   * ohne Datenbankzugriff), bekommen aber trotzdem einen eigenen Key statt
   * eines Literal-Arrays am Aufrufer (Regel 1 oben). */
  documents: {
    all: ['documents'] as const,
    list: (filter: Readonly<Record<string, unknown>>) => ['documents', 'list', filter] as const,
    summary: ['documents', 'summary'] as const,
    categories: ['documents', 'categories'] as const,
    limits: ['documents', 'limits'] as const,
    /** Spec 44 — Kommentare eines Dokuments. Unter dem Präfix `documents`, damit
     * Dokumentmutationen sie mit erfassen; Kommentar-Mutationen invalidieren
     * umgekehrt **nur** diesen Key (ein Kommentar ändert keine Dokumentliste). */
    comments: (documentId: string) => ['documents', 'comments', documentId] as const,
  },

  /** Spec 46a — Zähler. Ein Präfix für Liste (samt Hausliste), Detail und
   * Auswahllisten: Anlegen und Ändern verändern Liste, `houses` (Zähler je
   * Haus) und Detail zugleich, deshalb invalidiert jede Mutation
   * ausschließlich über `energy.all`, nie einzeln aufgezählt. Die Preisabfragen
   * der Kapsel (iOS) kennen dieses Präfix im Web nicht. `meterOptions` ändert
   * sich nie zur Laufzeit (feste Aufzählung, keine Datenbankabfrage), bekommt
   * aber trotzdem einen eigenen Key (Regel 1 oben). */
  energy: {
    all: ['energy'] as const,
    meters: (filter: Readonly<Record<string, unknown>>) => ['energy', 'meters', filter] as const,
    meter: (id: string) => ['energy', 'meter', id] as const,
    meterOptions: ['energy', 'meter-options'] as const,
  },

  /** Spec 24c — Absender. Eigenes Präfix: Anlegen und Ändern eines Absenders
   * können Vorschläge an **bestehenden** Eingangsdokumenten auslösen
   * (Fachregel 3, Auslöser c) und ändern `document_count` an der Zeile —
   * `useCreateSender`/`useUpdateSender` invalidieren deshalb `senders.all`
   * **und** `documents.all`, nie einzeln aufgezählt (dasselbe Muster wie
   * `billing`/`tickets`). Umgekehrt ändert Übernehmen/Verwerfen an einem
   * Dokument (`features/documents/api.ts`) `document_count` am Absender —
   * dieselbe Mutation invalidiert deshalb auch über dieses Präfix. */
  senders: {
    all: ['senders'] as const,
    list: (filter: Readonly<Record<string, unknown>>) => ['senders', 'list', filter] as const,
    detail: (id: string) => ['senders', 'detail', id] as const,
  },
} as const
